import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Grid,
  Divider,
  Alert,
  FormHelperText,
  Chip,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  IconButton,
  Avatar,
  InputAdornment,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  Paper,
  useTheme,
  alpha,
  styled,
  Collapse,
  Badge,
  Fade,
  Tooltip,
  Checkbox,
  FormControlLabel,
  Snackbar
} from '@mui/material';
import {
  LocalOffer as OfferIcon,
  Warning as WarningIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  LocalShipping as ShippingIcon,
  CreditCard as PaymentIcon,
  Redeem as RedeemIcon,
  ShoppingCart as CartIcon,
  InfoOutlined as InfoIcon,
  Receipt as ReceiptIcon,
  SecurityOutlined as SecurityIcon,
  History as HistoryIcon,
  Add as AddIcon,
  Edit as EditIcon,
  DeleteOutline as DeleteIcon,
  Home as HomeIcon,
  LocationOn as LocationIcon,
  ContactMail as ContactIcon,
  ArrowForward as ArrowIcon,
  AccountBalanceWallet as WalletIcon,
  Email as EmailIcon,
  VerifiedUser as VerifiedIcon,
  EventNote as NoteIcon,
  Smartphone as PhoneIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';

// Custom styled components
const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
  color: theme.palette.primary.contrastText,
  padding: theme.spacing(3),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
}));

const CircleDecoration = styled('div')(({ theme, size = 100, top, right, left, bottom, opacity = 0.1 }) => ({
  position: 'absolute',
  width: size,
  height: size,
  borderRadius: '50%',
  background: alpha(theme.palette.primary.main, opacity),
  top: top || 'auto',
  right: right || 'auto',
  left: left || 'auto',
  bottom: bottom || 'auto',
  zIndex: 0,
}));

const ContentSection = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  backgroundColor: alpha(theme.palette.background.paper, 0.6),
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(2),
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
}));

const SummaryItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: theme.spacing(1.5),
  '&:not(:last-child)': {
    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
  },
}));

const StyledStepLabel = styled(StepLabel)(({ theme }) => ({
  '& .MuiStepLabel-iconContainer': {
    '& .MuiSvgIcon-root': {
      fontSize: 28,
    },
  },
  '& .MuiStepLabel-label': {
    marginTop: theme.spacing(1),
    fontWeight: 'medium',
    '&.Mui-active': {
      fontWeight: 'bold',
      color: theme.palette.primary.main,
    },
    '&.Mui-completed': {
      color: theme.palette.success.main,
    }
  },
}));

const AddressCard = styled(Paper)(({ theme, selected }) => ({
  padding: theme.spacing(2),
  borderRadius: theme.spacing(1),
  border: `1px solid ${selected ? theme.palette.primary.main : alpha(theme.palette.divider, 0.5)}`,
  backgroundColor: selected ? alpha(theme.palette.primary.main, 0.05) : theme.palette.background.paper,
  boxShadow: selected ? `0 0 0 2px ${alpha(theme.palette.primary.main, 0.3)}` : 'none',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  '&:hover': {
    borderColor: selected ? theme.palette.primary.main : alpha(theme.palette.primary.main, 0.5),
    boxShadow: `0 3px 10px ${alpha(theme.palette.common.black, 0.1)}`
  }
}));

const CheckoutSystem = ({ 
  open, 
  onClose, 
  item, 
  onSubmit, 
  walletBalance,
  userProfile 
}) => {
  const theme = useTheme();
  const [currentStep, setCurrentStep] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [notes, setNotes] = useState('');
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [orderNumber, setOrderNumber] = useState('');
  const [receiptUrl, setReceiptUrl] = useState('');
  const [addresses, setAddresses] = useState([]);
  const [selectedAddressIndex, setSelectedAddressIndex] = useState(-1);
  const [showAddAddressForm, setShowAddAddressForm] = useState(false);
  const [savedForLater, setSavedForLater] = useState(false);
  const [deliveryMethod, setDeliveryMethod] = useState('standard');
  const [giftOption, setGiftOption] = useState(false);
  const [giftMessage, setGiftMessage] = useState('');
  const [confirmationEmail, setConfirmationEmail] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [paymentProcessingAnimation, setPaymentProcessingAnimation] = useState(false);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'info' });
  
  // For physical items that need shipping
  const [newAddress, setNewAddress] = useState({
    name: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: '',
    phoneNumber: ''
  });

  // Reset all form fields when the modal opens
  useEffect(() => {
    if (open) {
      setCurrentStep(0);
      setQuantity(1);
      setNotes('');
      setErrors({});
      setSuccess(false);
      setProcessing(false);
      setOrderNumber('');
      setReceiptUrl('');
      setSelectedAddressIndex(-1);
      setShowAddAddressForm(false);
      setSavedForLater(false);
      setDeliveryMethod('standard');
      setGiftOption(false);
      setGiftMessage('');
      setConfirmationEmail(userProfile?.email || '');
      setTermsAccepted(false);
      
      // Load addresses
      loadUserAddresses();
    }
  }, [open, userProfile]);

  // Load user addresses (mock implementation)
  const loadUserAddresses = () => {
    // In a real app, this would fetch from your API
    const mockAddresses = [
      {
        id: 1,
        name: userProfile?.firstName + ' ' + userProfile?.lastName,
        address: '123 Main Street',
        city: 'Boston',
        state: 'MA',
        zipCode: '02108',
        country: 'US',
        phoneNumber: '617-555-1234',
        isDefault: true
      }
    ];
    
    setAddresses(mockAddresses);
    if (mockAddresses.length > 0) {
      const defaultIndex = mockAddresses.findIndex(addr => addr.isDefault);
      setSelectedAddressIndex(defaultIndex >= 0 ? defaultIndex : 0);
    }
  };

  const handleAddressChange = (index) => {
    setSelectedAddressIndex(index);
    setShowAddAddressForm(false);
  };

  const handleNewAddressChange = (field) => (event) => {
    setNewAddress({
      ...newAddress,
      [field]: event.target.value
    });
  };

  const handleAddNewAddress = () => {
    setShowAddAddressForm(true);
    setSelectedAddressIndex(-1);
  };

  const handleSaveNewAddress = () => {
    // Validate first
    const newErrors = validateAddress(newAddress);
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    // Add to the list of addresses
    const newAddressList = [...addresses, { 
      ...newAddress, 
      id: addresses.length + 1,
      isDefault: addresses.length === 0
    }];
    
    setAddresses(newAddressList);
    setSelectedAddressIndex(newAddressList.length - 1);
    setShowAddAddressForm(false);
    
    setNotification({
      open: true,
      message: 'New address saved successfully',
      severity: 'success'
    });
  };

  const validateAddress = (address) => {
    const validationErrors = {};
    
    if (!address.name) validationErrors.name = 'Name is required';
    if (!address.address) validationErrors.address = 'Address is required';
    if (!address.city) validationErrors.city = 'City is required';
    if (!address.state) validationErrors.state = 'State is required';
    if (!address.zipCode) validationErrors.zipCode = 'ZIP code is required';
    if (!address.country) validationErrors.country = 'Country is required';
    
    return validationErrors;
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Only validate current step's fields
    if (currentStep === 0) {
      // Validate quantity
      if (quantity < 1) {
        newErrors.quantity = 'Quantity must be at least 1';
      }
      
      if (item.quantity && quantity > item.quantity) {
        newErrors.quantity = `Only ${item.quantity} items available`;
      }
      
      // Check if user has enough tokens
      if (walletBalance < (item.tokenPrice * quantity)) {
        newErrors.wallet = `Insufficient tokens. You need ${item.tokenPrice * quantity} tokens`;
      }
    }
    
    // Validate shipping details on the shipping step for physical items
    if (currentStep === 1 && item.isPhysical) {
      if (selectedAddressIndex === -1 && !showAddAddressForm) {
        newErrors.address = 'Please select a shipping address';
      }
      
      if (showAddAddressForm) {
        Object.assign(newErrors, validateAddress(newAddress));
      }
    }
    
    // Validate the final confirmation step
    if (currentStep === 2) {
      if (!termsAccepted) {
        newErrors.terms = 'You must accept the terms and conditions';
      }
      
      if (confirmationEmail && !/\S+@\S+\.\S+/.test(confirmationEmail)) {
        newErrors.email = 'Please enter a valid email address';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateForm()) {
      if (currentStep < getSteps().length - 1) {
        setCurrentStep(currentStep + 1);
        window.scrollTo(0, 0);
      }
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
      window.scrollTo(0, 0);
    }
  };

  const handleSubmit = async () => {
    if (validateForm()) {
      setLoading(true);
      setProcessing(true);
      setPaymentProcessingAnimation(true);
      
      try {
        // Simulate payment processing
        await new Promise(resolve => setTimeout(resolve, 2500));
        
        // Generate a mock order number
        const orderNum = 'ORD-' + Math.floor(100000 + Math.random() * 900000);
        setOrderNumber(orderNum);
        
        // Create data object to submit
        const submissionData = {
          itemId: item._id,
          quantity,
          shippingDetails: item.isPhysical ? (
            selectedAddressIndex >= 0 ? addresses[selectedAddressIndex] : newAddress
          ) : null,
          notes,
          deliveryMethod,
          giftOption,
          giftMessage: giftOption ? giftMessage : '',
          email: confirmationEmail,
          orderNumber: orderNum,
          timestamp: new Date().toISOString()
        };
        
        // Submit to parent component
        await onSubmit(submissionData);
        
        // Success state
        setSuccess(true);
        setNotification({
          open: true,
          message: 'Order placed successfully!',
          severity: 'success'
        });
        
        setTimeout(() => {
          // In a real app, you might fetch the actual receipt URL from your backend
          setReceiptUrl(`/receipts/${orderNum}`);
        }, 1000);
        
      } catch (error) {
        console.error("Checkout error:", error);
        setErrors({ submit: error.message || 'Failed to complete checkout' });
        setNotification({
          open: true,
          message: 'Checkout failed: ' + (error.message || 'Please try again'),
          severity: 'error'
        });
      } finally {
        setLoading(false);
        setProcessing(false);
        setPaymentProcessingAnimation(false);
      }
    }
  };

  const getSteps = () => {
    if (item.isPhysical) {
      return [
        { label: 'Review Item', icon: <CartIcon /> },
        { label: 'Shipping', icon: <ShippingIcon /> },
        { label: 'Confirmation', icon: <ReceiptIcon /> }
      ];
    } else {
      return [
        { label: 'Review Item', icon: <CartIcon /> },
        { label: 'Confirmation', icon: <ReceiptIcon /> }
      ];
    }
  };

  const totalCost = quantity * item.tokenPrice;
  const remainingBalance = walletBalance - totalCost;
  
  const getDeliveryDate = () => {
    const today = new Date();
    let deliveryDays;
    
    switch (deliveryMethod) {
      case 'express':
        deliveryDays = 2;
        break;
      case 'priority':
        deliveryDays = 3;
        break;
      case 'standard':
      default:
        deliveryDays = 5;
        break;
    }
    
    const deliveryDate = new Date(today);
    deliveryDate.setDate(today.getDate() + deliveryDays);
    
    return deliveryDate.toLocaleDateString('en-US', { 
      weekday: 'long', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const getDeliveryCost = () => {
    return 0; // Always free in token economy
  };

  const handleNotificationClose = () => {
    setNotification({ ...notification, open: false });
  };

  return (
    <>
      <Dialog 
        open={open} 
        onClose={!processing ? onClose : undefined} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          component: motion.div,
          initial: { opacity: 0, y: 20 },
          animate: { opacity: 1, y: 0 },
          transition: { duration: 0.3 },
          sx: { 
            borderRadius: 3, 
            overflow: 'visible', // Changed from 'hidden' to 'visible'
            maxHeight: '90vh'    // Added max height
          }
        }}
        scroll="paper" // Set scroll behavior
      >
        <StyledDialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Avatar 
              sx={{ 
                bgcolor: 'rgba(255, 255, 255, 0.2)', 
                mr: 2, 
                color: 'white',
              }}
            >
              {success ? <CheckIcon /> : <CartIcon />}
            </Avatar>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              {success ? 'Order Complete!' : `Checkout: ${item?.name}`}
            </Typography>
          </Box>
          <IconButton 
            onClick={onClose} 
            sx={{ color: 'white' }}
            disabled={loading || processing}
          >
            <CloseIcon />
          </IconButton>
        </StyledDialogTitle>
        
        <Box sx={{ position: 'relative' }}>
          <CircleDecoration size={200} top="-100px" right="-100px" opacity={0.03} />
          <CircleDecoration size={150} bottom="-50px" left="-50px" opacity={0.03} />
          
          <DialogContent 
            sx={{ 
              p: 3, 
              position: 'relative',
              overflowY: 'auto',      // Ensure content can scroll
              maxHeight: 'calc(80vh - 160px)'  // Adjusted to leave space for actions
            }}
          >
            {success ? (
              <Box 
                component={motion.div}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5 }}
                sx={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center', 
                  py: 4 
                }}
              >
                <Avatar
                  sx={{
                    bgcolor: theme.palette.success.main,
                    width: 80,
                    height: 80,
                    mb: 2,
                    boxShadow: `0 0 0 8px ${alpha(theme.palette.success.main, 0.2)}`
                  }}
                >
                  <CheckIcon sx={{ fontSize: 40 }} />
                </Avatar>
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
                  Order Successful!
                </Typography>
                <Typography variant="body1" align="center" color="text.secondary" sx={{ maxWidth: 400, mb: 2 }}>
                  Your order #{orderNumber} has been confirmed. You will receive a confirmation email shortly.
                </Typography>
                
                <Box sx={{ 
                  width: '100%', 
                  maxWidth: 500,
                  mt: 3,
                  p: 3,
                  bgcolor: alpha(theme.palette.success.main, 0.05),
                  border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                  borderRadius: 2,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
                }}>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: theme.palette.success.main }}>
                    Order Summary
                  </Typography>
                  
                  <SummaryItem>
                    <Typography variant="body2" color="text.secondary">Order Number</Typography>
                    <Typography variant="body1" fontWeight="bold">{orderNumber}</Typography>
                  </SummaryItem>
                  
                  <SummaryItem>
                    <Typography variant="body2" color="text.secondary">Item</Typography>
                    <Typography variant="body1">{item.name}</Typography>
                  </SummaryItem>
                  
                  <SummaryItem>
                    <Typography variant="body2" color="text.secondary">Quantity</Typography>
                    <Typography variant="body1">{quantity}</Typography>
                  </SummaryItem>
                  
                  <SummaryItem>
                    <Typography variant="body2" color="text.secondary">Total</Typography>
                    <Typography variant="body1" fontWeight="bold" color="primary">
                      {totalCost} tokens
                    </Typography>
                  </SummaryItem>
                  
                  {item.isPhysical && (
                    <SummaryItem>
                      <Typography variant="body2" color="text.secondary">Estimated Delivery</Typography>
                      <Typography variant="body1">{getDeliveryDate()}</Typography>
                    </SummaryItem>
                  )}
                </Box>
                
                <Chip 
                  icon={<VerifiedIcon />}
                  label="Tokens were deducted from your wallet"
                  color="primary"
                  variant="outlined"
                  sx={{ mt: 3, mb: 1 }}
                />
              </Box>
            ) : (
              <>
                <Stepper 
                  activeStep={currentStep} 
                  alternativeLabel 
                  sx={{ 
                    mb: 4,
                    '& .MuiStepLabel-label': {
                      mt: 1
                    }
                  }}
                >
                  {getSteps().map((step, index) => (
                    <Step key={index}>
                      <StyledStepLabel StepIconProps={{ 
                        icon: step.icon,
                        sx: { 
                          color: currentStep >= index 
                            ? theme.palette.primary.main 
                            : theme.palette.grey[400]
                        }
                      }}>
                        {step.label}
                      </StyledStepLabel>
                    </Step>
                  ))}
                </Stepper>
                
                <AnimatePresence mode="wait">
                  {currentStep === 0 && (
                    <Box 
                      component={motion.div} 
                      key="step-0"
                      initial={{ opacity: 0 }} 
                      animate={{ opacity: 1 }} 
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.5 }}
                    >
                      <Grid container spacing={3}>
                        {/* Item Information */}
                        <Grid item xs={12} md={6}>
                          <ContentSection>
                            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
                              <InfoIcon sx={{ mr: 1, fontSize: 20, color: theme.palette.primary.main }} />
                              Item Details
                            </Typography>
                            
                            <Box sx={{ display: 'flex', mb: 2, borderRadius: 2, overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
                              {item.imageUrl ? (
                                <Box 
                                  component="img" 
                                  src={item.imageUrl}
                                  alt={item.name}
                                  sx={{ 
                                    width: '100%', 
                                    height: 160, 
                                    objectFit: 'cover',
                                    borderRadius: 2 
                                  }}
                                />
                              ) : (
                                <Box 
                                  sx={{ 
                                    width: '100%', 
                                    height: 160, 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    justifyContent: 'center',
                                    bgcolor: alpha(theme.palette.primary.main, 0.05),
                                    borderRadius: 2
                                  }}
                                >
                                  <RedeemIcon sx={{ fontSize: 60, color: alpha(theme.palette.primary.main, 0.3) }} />
                                </Box>
                              )}
                            </Box>
                            
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                              <Chip 
                                icon={<OfferIcon />} 
                                label={`${item.tokenPrice} tokens per item`}
                                color="primary"
                                sx={{ mr: 1, fontWeight: 'bold' }}
                              />
                              {item.category && (
                                <Chip label={item.category} size="small" variant="outlined" />
                              )}
                            </Box>
                            
                            <Typography variant="h6" gutterBottom>
                              {item.name}
                            </Typography>
                            
                            <Typography variant="body1" paragraph>
                              {item.description}
                            </Typography>
                            
                            {item.quantity !== undefined && item.quantity <= 5 && (
                              <Alert 
                                severity="warning" 
                                icon={<WarningIcon />}
                                sx={{ mb: 2, borderRadius: 2 }}
                              >
                                Only {item.quantity} items left in stock!
                              </Alert>
                            )}
                          </ContentSection>
                        </Grid>
                        
                        {/* Quantity Selection */}
                        <Grid item xs={12} md={6}>
                          <ContentSection>
                            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
                              <CartIcon sx={{ mr: 1, fontSize: 20, color: theme.palette.primary.main }} />
                              Quantity & Cost
                            </Typography>
                            
                            <TextField
                              label="Quantity"
                              type="number"
                              fullWidth
                              value={quantity}
                              onChange={(e) => setQuantity(parseInt(e.target.value) || 1)}
                              InputProps={{ 
                                inputProps: { min: 1, max: item.quantity },
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <CartIcon fontSize="small" />
                                  </InputAdornment>
                                )
                              }}
                              error={!!errors.quantity}
                              helperText={errors.quantity}
                              sx={{ mb: 3 }}
                            />
                            
                            <Paper 
                              variant="outlined" 
                              sx={{ 
                                p: 2, 
                                mb: 3, 
                                bgcolor: alpha(theme.palette.background.paper, 0.5),
                                borderRadius: 2 
                              }}
                            >
                              <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                                Cost Summary:
                              </Typography>
                              
                              <SummaryItem>
                                <Typography variant="body2">Item Cost:</Typography>
                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                  {item.tokenPrice} × {quantity} = {totalCost} tokens
                                </Typography>
                              </SummaryItem>
                              
                              <SummaryItem>
                                <Typography variant="body2">Your Balance:</Typography>
                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                  {walletBalance} tokens
                                </Typography>
                              </SummaryItem>
                              
                              <SummaryItem>
                                <Typography variant="body2">Remaining Balance:</Typography>
                                <Typography 
                                  variant="body2" 
                                  sx={{ 
                                    fontWeight: 'bold',
                                    color: remainingBalance < 0 ? theme.palette.error.main : 'inherit'
                                  }}
                                >
                                  {remainingBalance} tokens
                                </Typography>
                              </SummaryItem>
                            </Paper>
                            
                            {errors.wallet && (
                              <Alert 
                                severity="error" 
                                sx={{ mb: 2, borderRadius: 2 }}
                              >
                                {errors.wallet}
                              </Alert>
                            )}
                            
                            <TextField
                              label="Notes (Optional)"
                              multiline
                              rows={2}
                              fullWidth
                              value={notes}
                              onChange={(e) => setNotes(e.target.value)}
                              placeholder="Special requests or comments about your order..."
                              sx={{ mb: 2 }}
                            />
                            
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={savedForLater}
                                  onChange={(e) => setSavedForLater(e.target.checked)}
                                  color="primary"
                                />
                              }
                              label="Save this to favorites for later"
                            />
                          </ContentSection>
                        </Grid>
                      </Grid>
                    </Box>
                  )}
                  
                  {currentStep === 1 && item.isPhysical && (
                    <Box 
                      component={motion.div} 
                      key="step-1a"
                      initial={{ opacity: 0 }} 
                      animate={{ opacity: 1 }} 
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.5 }}
                    >
                      <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
                        <ShippingIcon sx={{ mr: 1 }} />
                        Shipping Details
                      </Typography>
                      
                      <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
                        Please select a shipping address for this physical item.
                      </Alert>
                      
                      {addresses.length > 0 && (
                        <Box sx={{ mb: 3 }}>
                          <Typography variant="subtitle1" gutterBottom>
                            Your Saved Addresses
                          </Typography>
                          
                          <Grid container spacing={2}>
                            {addresses.map((address, index) => (
                              <Grid item xs={12} md={6} key={address.id}>
                                <AddressCard 
                                  selected={selectedAddressIndex === index} 
                                  onClick={() => handleAddressChange(index)}
                                >
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                    <Typography variant="subtitle1" fontWeight="bold">
                                      {address.name}
                                    </Typography>
                                    {address.isDefault && (
                                      <Chip label="Default" size="small" color="primary" />
                                    )}
                                  </Box>
                                  
                                  <Typography variant="body2" color="text.secondary">
                                    {address.address}<br />
                                    {address.city}, {address.state} {address.zipCode}<br />
                                    {address.country}
                                  </Typography>
                                  
                                  {address.phoneNumber && (
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                      <PhoneIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
                                      {address.phoneNumber}
                                    </Typography>
                                  )}
                                </AddressCard>
                              </Grid>
                            ))}
                            
                            <Grid item xs={12} md={6}>
                              <AddressCard 
                                onClick={handleAddNewAddress}
                                sx={{ 
                                  height: '100%', 
                                  display: 'flex', 
                                  flexDirection: 'column',
                                  justifyContent: 'center',
                                  alignItems: 'center',
                                  border: `1px dashed ${theme.palette.divider}`,
                                  boxShadow: 'none'
                                }}
                              >
                                <AddIcon sx={{ fontSize: 32, color: theme.palette.text.secondary, mb: 1 }} />
                                <Typography variant="body1" color="text.secondary">
                                  Add New Address
                                </Typography>
                              </AddressCard>
                            </Grid>
                          </Grid>
                        </Box>
                      )}
                      
                      <Collapse in={showAddAddressForm}>
                        <Paper variant="outlined" sx={{ p: 3, mb: 3, borderRadius: 2 }}>
                          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                            Add New Address
                          </Typography>
                          
                          <Grid container spacing={2}>
                            <Grid item xs={12}>
                              <TextField
                                label="Full Name"
                                fullWidth
                                value={newAddress.name}
                                onChange={handleNewAddressChange('name')}
                                error={!!errors.name}
                                helperText={errors.name}
                                required
                                sx={{ mb: 2 }}
                                InputProps={{
                                  startAdornment: (
                                    <InputAdornment position="start">
                                      <ContactIcon fontSize="small" />
                                    </InputAdornment>
                                  )
                                }}
                              />
                            </Grid>
                            
                            <Grid item xs={12}>
                              <TextField
                                label="Address"
                                fullWidth
                                value={newAddress.address}
                                onChange={handleNewAddressChange('address')}
                                error={!!errors.address}
                                helperText={errors.address}
                                required
                                sx={{ mb: 2 }}
                                InputProps={{
                                  startAdornment: (
                                    <InputAdornment position="start">
                                      <HomeIcon fontSize="small" />
                                    </InputAdornment>
                                  )
                                }}
                              />
                            </Grid>
                            
                            <Grid item xs={12} sm={6}>
                              <TextField
                                label="City"
                                fullWidth
                                value={newAddress.city}
                                onChange={handleNewAddressChange('city')}
                                error={!!errors.city}
                                helperText={errors.city}
                                required
                                sx={{ mb: 2 }}
                              />
                            </Grid>
                            
                            <Grid item xs={12} sm={6}>
                              <TextField
                                label="State/Province"
                                fullWidth
                                value={newAddress.state}
                                onChange={handleNewAddressChange('state')}
                                error={!!errors.state}
                                helperText={errors.state}
                                required
                                sx={{ mb: 2 }}
                              />
                            </Grid>
                            
                            <Grid item xs={12} sm={6}>
                              <TextField
                                label="ZIP/Postal Code"
                                fullWidth
                                value={newAddress.zipCode}
                                onChange={handleNewAddressChange('zipCode')}
                                error={!!errors.zipCode}
                                helperText={errors.zipCode}
                                required
                                sx={{ mb: 2 }}
                              />
                            </Grid>
                            
                            <Grid item xs={12} sm={6}>
                              <FormControl fullWidth error={!!errors.country} sx={{ mb: 2 }}>
                                <InputLabel id="country-select-label">Country</InputLabel>
                                <Select
                                  labelId="country-select-label"
                                  value={newAddress.country}
                                  onChange={handleNewAddressChange('country')}
                                  label="Country"
                                  required
                                >
                                  <MenuItem value="US">United States</MenuItem>
                                  <MenuItem value="CA">Canada</MenuItem>
                                  <MenuItem value="UK">United Kingdom</MenuItem>
                                  <MenuItem value="AU">Australia</MenuItem>
                                  <MenuItem value="Other">Other</MenuItem>
                                </Select>
                                {errors.country && <FormHelperText>{errors.country}</FormHelperText>}
                              </FormControl>
                            </Grid>
                            
                            <Grid item xs={12}>
                              <TextField
                                label="Phone Number"
                                fullWidth
                                value={newAddress.phoneNumber}
                                onChange={handleNewAddressChange('phoneNumber')}
                                placeholder="For delivery questions"
                                sx={{ mb: 2 }}
                                InputProps={{
                                  startAdornment: (
                                    <InputAdornment position="start">
                                      <PhoneIcon fontSize="small" />
                                    </InputAdornment>
                                  )
                                }}
                              />
                            </Grid>
                          </Grid>
                          
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                            <Button 
                              onClick={() => setShowAddAddressForm(false)} 
                              sx={{ mr: 1 }}
                            >
                              Cancel
                            </Button>
                            <Button 
                              variant="contained"
                              onClick={handleSaveNewAddress}
                            >
                              Save Address
                            </Button>
                          </Box>
                        </Paper>
                      </Collapse>
                      
                      {!showAddAddressForm && selectedAddressIndex === -1 && addresses.length === 0 && (
                        <Alert severity="warning" sx={{ mb: 3 }}>
                          Please add a shipping address to continue
                        </Alert>
                      )}
                      
                      <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', mt: 4, display: 'flex', alignItems: 'center' }}>
                        <ShippingIcon sx={{ mr: 1 }} />
                        Delivery Options
                      </Typography>
                      
                      <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid item xs={12} md={4}>
                          <AddressCard 
                            selected={deliveryMethod === 'standard'}
                            onClick={() => setDeliveryMethod('standard')}
                          >
                            <Typography variant="subtitle1" fontWeight="bold">
                              Standard Delivery
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              5 business days
                            </Typography>
                            <Chip 
                              label="Free" 
                              size="small" 
                              color="success" 
                              sx={{ mt: 1 }}
                            />
                          </AddressCard>
                        </Grid>
                        
                        <Grid item xs={12} md={4}>
                          <AddressCard 
                            selected={deliveryMethod === 'priority'}
                            onClick={() => setDeliveryMethod('priority')}
                          >
                            <Typography variant="subtitle1" fontWeight="bold">
                              Priority Delivery
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              3 business days
                            </Typography>
                            <Chip 
                              label="Free" 
                              size="small" 
                              color="success" 
                              sx={{ mt: 1 }}
                            />
                          </AddressCard>
                        </Grid>
                        
                        <Grid item xs={12} md={4}>
                          <AddressCard 
                            selected={deliveryMethod === 'express'}
                            onClick={() => setDeliveryMethod('express')}
                          >
                            <Typography variant="subtitle1" fontWeight="bold">
                              Express Delivery
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              1-2 business days
                            </Typography>
                            <Chip 
                              label="Free" 
                              size="small" 
                              color="success" 
                              sx={{ mt: 1 }}
                            />
                          </AddressCard>
                        </Grid>
                      </Grid>
                      
                      <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={giftOption}
                              onChange={(e) => setGiftOption(e.target.checked)}
                              color="primary"
                            />
                          }
                          label="This is a gift"
                        />
                        
                        <Collapse in={giftOption}>
                          <TextField
                            label="Gift Message"
                            multiline
                            rows={3}
                            fullWidth
                            value={giftMessage}
                            onChange={(e) => setGiftMessage(e.target.value)}
                            placeholder="Add a personalized message to your gift..."
                            sx={{ mt: 2 }}
                          />
                        </Collapse>
                      </Paper>
                    </Box>
                  )}
                  
                  {currentStep === 1 && !item.isPhysical && (
                    <Box 
                      component={motion.div} 
                      key="step-1b"
                      initial={{ opacity: 0 }} 
                      animate={{ opacity: 1 }} 
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.5 }}
                    >
                      <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
                        <InfoIcon sx={{ mr: 1 }} />
                        Delivery Details
                      </Typography>
                      
                      <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
                        This is a digital item and will be delivered to your account after redemption.
                      </Alert>
                      
                      <Paper variant="outlined" sx={{ p: 3, mb: 3, borderRadius: 2 }}>
                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                          Digital Delivery
                        </Typography>
                        
                        <Typography variant="body2" paragraph>
                          You will receive access to this digital item immediately after confirmation.
                          {item.description && ` ${item.description}`}
                        </Typography>
                        
                        <TextField
                          label="Email for Receipt (Optional)"
                          fullWidth
                          value={confirmationEmail}
                          onChange={(e) => setConfirmationEmail(e.target.value)}
                          error={!!errors.email}
                          helperText={errors.email}
                          placeholder="Email for confirmation (optional)"
                          sx={{ mt: 2 }}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <EmailIcon fontSize="small" />
                              </InputAdornment>
                            )
                          }}
                        />
                      </Paper>
                      
                      <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', mt: 4, display: 'flex', alignItems: 'center' }}>
                        <NoteIcon sx={{ mr: 1 }} />
                        Special Instructions
                      </Typography>
                      
                      <TextField
                        label="Additional Notes (Optional)"
                        multiline
                        rows={3}
                        fullWidth
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        placeholder="Any special requests or notes about your digital redemption..."
                        sx={{ mb: 3 }}
                      />
                      
                      <Alert 
                        severity="success" 
                        icon={<VerifiedIcon />}
                        sx={{ mb: 2 }}
                      >
                        Instant delivery - redeem this item immediately!
                      </Alert>
                    </Box>
                  )}
                  
                  {(currentStep === 2 || (currentStep === 1 && !item.isPhysical && getSteps().length === 2)) && (
                    <Box 
                      component={motion.div} 
                      key="step-2"
                      initial={{ opacity: 0 }} 
                      animate={{ opacity: 1 }} 
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.5 }}
                    >
                      <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
                        <ReceiptIcon sx={{ mr: 1 }} />
                        Order Confirmation
                      </Typography>
                      
                      <Paper 
                        variant="outlined" 
                        sx={{ 
                          p: 3, 
                          mb: 3, 
                          borderRadius: 2,
                          bgcolor: alpha(theme.palette.primary.main, 0.03)
                        }}
                      >
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="subtitle2" gutterBottom color="text.secondary">
                              Item
                            </Typography>
                            <Typography variant="body1" gutterBottom sx={{ fontWeight: 'bold' }}>
                              {item.name}
                            </Typography>
                            
                            <Typography variant="subtitle2" gutterBottom color="text.secondary" sx={{ mt: 2 }}>
                              Quantity
                            </Typography>
                            <Typography variant="body1" gutterBottom sx={{ fontWeight: 'bold' }}>
                              {quantity}
                            </Typography>
                            
                            <Typography variant="subtitle2" gutterBottom color="text.secondary" sx={{ mt: 2 }}>
                              Type
                            </Typography>
                            <Chip 
                              label={item.isPhysical ? 'Physical Item' : 'Digital Item'} 
                              size="small"
                              color={item.isPhysical ? 'secondary' : 'primary'}
                              icon={item.isPhysical ? <ShippingIcon /> : <InfoIcon />}
                            />
                          </Grid>
                          
                          <Grid item xs={12} sm={6}>
                            <Typography variant="subtitle2" gutterBottom color="text.secondary">
                              Cost
                            </Typography>
                            <Typography variant="h5" gutterBottom color="primary" sx={{ fontWeight: 'bold' }}>
                              {totalCost} tokens
                            </Typography>
                            
                            <Typography variant="subtitle2" gutterBottom color="text.secondary" sx={{ mt: 2 }}>
                              Your Balance After Order
                            </Typography>
                            <Typography 
                              variant="body1" 
                              gutterBottom 
                              sx={{ 
                                fontWeight: 'bold',
                                color: remainingBalance < 0 ? 'error.main' : 'success.main' 
                              }}
                            >
                              {remainingBalance} tokens
                            </Typography>
                            
                            {item.isPhysical && (
                              <>
                                <Typography variant="subtitle2" gutterBottom color="text.secondary" sx={{ mt: 2 }}>
                                  Delivery Method
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                  <Chip 
                                    label={deliveryMethod.charAt(0).toUpperCase() + deliveryMethod.slice(1)} 
                                    size="small"
                                    color="primary"
                                    sx={{ mr: 1 }}
                                  />
                                  <Typography variant="body2" color="text.secondary">
                                    (Estimated: {getDeliveryDate()})
                                  </Typography>
                                </Box>
                              </>
                            )}
                          </Grid>
                          
                          {item.isPhysical && selectedAddressIndex >= 0 && (
                            <Grid item xs={12}>
                              <Divider sx={{ my: 2 }} />
                              <Typography variant="subtitle2" gutterBottom color="text.secondary">
                                Shipping Address
                              </Typography>
                              <Typography variant="body2">
                                {addresses[selectedAddressIndex].name}<br />
                                {addresses[selectedAddressIndex].address}<br />
                                {addresses[selectedAddressIndex].city}, {addresses[selectedAddressIndex].state} {addresses[selectedAddressIndex].zipCode}<br />
                                {addresses[selectedAddressIndex].country}
                                {addresses[selectedAddressIndex].phoneNumber && (
                                  <><br />Phone: {addresses[selectedAddressIndex].phoneNumber}</>
                                )}
                              </Typography>
                            </Grid>
                          )}
                          
                          {notes && (
                            <Grid item xs={12}>
                              <Divider sx={{ my: 2 }} />
                              <Typography variant="subtitle2" gutterBottom color="text.secondary">
                                Notes
                              </Typography>
                              <Typography variant="body2">
                                {notes}
                              </Typography>
                            </Grid>
                          )}
                          
                          {giftOption && giftMessage && (
                            <Grid item xs={12}>
                              <Divider sx={{ my: 2 }} />
                              <Typography variant="subtitle2" gutterBottom color="text.secondary">
                                Gift Message
                              </Typography>
                              <Paper variant="outlined" sx={{ p: 2, borderStyle: 'dashed' }}>
                                <Typography variant="body2" fontStyle="italic">
                                  "{giftMessage}"
                                </Typography>
                              </Paper>
                            </Grid>
                          )}
                        </Grid>
                      </Paper>
                      
                      <TextField
                        label="Email for Receipt"
                        fullWidth
                        value={confirmationEmail}
                        onChange={(e) => setConfirmationEmail(e.target.value)}
                        placeholder="Where to send your receipt (optional)"
                        sx={{ mb: 3 }}
                        error={!!errors.email}
                        helperText={errors.email}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <EmailIcon fontSize="small" />
                            </InputAdornment>
                          )
                        }}
                      />
                      
                      <FormControlLabel
                        control={
                          <Checkbox 
                            checked={termsAccepted}
                            onChange={(e) => setTermsAccepted(e.target.checked)}
                            color="primary"
                          />
                        }
                        label="I accept the terms and conditions"
                        sx={{ mb: 2 }}
                      />
                      
                      {errors.terms && (
                        <FormHelperText error sx={{ mb: 2 }}>
                          {errors.terms}
                        </FormHelperText>
                      )}
                      
                      <Alert 
                        severity="warning" 
                        sx={{ mb: 2, borderRadius: 2 }}
                      >
                        This transaction will deduct {totalCost} tokens from your balance. This action cannot be undone.
                      </Alert>
                      
                      {errors.submit && (
                        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                          {errors.submit}
                        </Alert>
                      )}
                    </Box>
                  )}
                </AnimatePresence>
              </>
            )}
          </DialogContent>
          
          <DialogActions 
            sx={{ 
              px: 3, 
              pb: 3, 
              pt: 2,       // Added top padding
              position: 'relative',
              borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`, // Added separator
              bgcolor: theme.palette.background.paper, // Added background
              display: 'flex',
              justifyContent: 'space-between'  // Better spacing for buttons
            }}
          >
            {success ? (
              <Button 
                onClick={onClose} 
                variant="contained"
                color="primary"
                fullWidth
                sx={{ py: 1.2, borderRadius: 2, fontWeight: 'bold' }}
              >
                Done
              </Button>
            ) : (
              <>
                <Box>
                  <Button 
                    onClick={onClose} 
                    color="inherit"
                    disabled={loading || processing}
                    sx={{ mr: 1 }}
                  >
                    Cancel
                  </Button>
                
                  {currentStep > 0 && (
                    <Button 
                      onClick={handleBack} 
                      disabled={loading || processing}
                      startIcon={<ArrowBackIcon />}
                    >
                      Back
                    </Button>
                  )}
                </Box>
                
                <Box>
                  {((currentStep < getSteps().length - 1) || 
                    (currentStep === 0 && !item.isPhysical && getSteps().length === 2)) ? (
                    <Button 
                      variant="contained" 
                      onClick={handleNext}
                      disabled={loading || processing}
                      endIcon={<ArrowForwardIcon />}
                      sx={{ minWidth: '100px' }}
                    >
                      Next
                    </Button>
                  ) : (
                    <Button 
                      variant="contained" 
                      color="primary"
                      onClick={handleSubmit}
                      disabled={loading || processing || !item || walletBalance < (item.tokenPrice * quantity)}
                      startIcon={loading || processing ? <CircularProgress size={20} /> : <CheckIcon />}
                      sx={{ minWidth: '140px' }}
                    >
                      {loading || processing ? 'Processing...' : 'Complete Order'}
                    </Button>
                  )}
                </Box>
              </>
            )}
          </DialogActions>
        </Box>
      </Dialog>
      
      {/* Payment Processing Overlay (shown while processing payment) */}
      <Dialog
        open={paymentProcessingAnimation}
        PaperProps={{
          sx: {
            bgcolor: 'transparent',
            boxShadow: 'none',
            overflow: 'hidden'
          }
        }}
      >
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center',
          justifyContent: 'center',
          p: 4,
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          borderRadius: 4,
          color: 'white'
        }}>
          <CircularProgress size={60} sx={{ mb: 2, color: 'white' }} />
          <Typography variant="h6" fontWeight="bold">
            Processing Payment
          </Typography>
          <Typography variant="body2" sx={{ mt: 1, textAlign: 'center' }}>
            Please wait while we process your token redemption...
          </Typography>
        </Box>
      </Dialog>
      
      {/* Notification Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleNotificationClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleNotificationClose} 
          severity={notification.severity} 
          variant="filled"
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default CheckoutSystem;